import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

// Focus/visibility refreshes within this window of the last fetch are skipped
// (returning to a tab fires both events, and DevTools clicks fire focus)
const MIN_REFRESH_GAP_MS = 30 * 1000;

/**
 * Custom hook for managing dashboard data lifecycle
 * Handles data fetching, caching, and refresh logic
 */
export const useDashboardData = (actions = []) => {
  const dispatch = useDispatch();

  // Keep the latest actions in a ref so an inline array doesn't make
  // refreshData change (and refetch everything) on every render
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const lastRefreshRef = useRef(0);

  const refreshData = useCallback(() => {
    const token = localStorage.getItem("token");
    const currentActions = actionsRef.current;
    if (token && currentActions.length > 0) {
      lastRefreshRef.current = Date.now();
      currentActions.forEach(action => {
        if (typeof action === 'function') {
          dispatch(action());
        } else if (action.actionCreator && action.payload !== undefined) {
          dispatch(action.actionCreator(action.payload));
        }
      });
    }
  }, [dispatch]);

  useEffect(() => {
    // Initial load
    refreshData();

    // Set up periodic refresh (every 5 minutes)
    const interval = setInterval(refreshData, 5 * 60 * 1000);

    const refreshIfStale = () => {
      if (Date.now() - lastRefreshRef.current >= MIN_REFRESH_GAP_MS) {
        refreshData();
      }
    };

    // Refresh when window gains focus
    const handleFocus = () => {
      refreshIfStale();
    };

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshIfStale();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshData]);

  return { refreshData };
};

/**
 * Custom hook for component-level data fetching with error handling
 */
export const useDataFetch = (actionCreator, dependencies = [], options = {}) => {
  const dispatch = useDispatch();
  const {
    skipInitialFetch = false,
    refetchOnMount = true,
    payload = undefined
  } = options;

  const fetchData = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token && actionCreator) {
      if (payload !== undefined) {
        dispatch(actionCreator(payload));
      } else {
        dispatch(actionCreator());
      }
    }
  }, [dispatch, actionCreator, payload]);

  useEffect(() => {
    if (!skipInitialFetch) {
      fetchData();
    }
  }, [fetchData, refetchOnMount, ...dependencies]);

  return { fetchData };
};